/**
 * The market lifecycle (D6).
 *
 * ```
 * SCHEDULED ─▶ FIXING ─▶ OPEN ─▶ EXPIRED ─▶ RESOLVING ─▶ RESOLVED ─▶ SETTLED
 *     │          │         │        │           │
 *     │          │         │        │           └─▶ RESOLVING_DEGRADED ─▶ RESOLVED
 *     └──────────┴─────────┴────────┴───────────────▶ VOIDED ─▶ REFUNDED
 *     └─▶ CANCELLED   (only from SCHEDULED/FIXING — no orders were ever accepted)
 * ```
 *
 * **Eleven states, and no twelfth.** The forward-looking settlement window (D5)
 * removes any need for a trading freeze: at the instant trading stops, the
 * settlement window has not started, so there is nothing to snipe and nothing to
 * lock. Trading runs to the expiry instant, and no state exists for a freeze
 * that does not happen. The pre-trade half is two distinct states rather than
 * one, for the reason below. Appendix C names both of the strings an earlier
 * 5-minute draft used here, and the lint rule keeps them out.
 *
 * `FIXING` is a distinct state rather than part of `SCHEDULED` because the UI
 * must say something different — `SCHEDULED` is "starts in 2:47", `FIXING` is
 * "fixing strike — 6s", and they are three minutes apart in user experience.
 *
 * **It is no longer simultaneous with the previous market's `RESOLVING`**
 * (register §F24). It was: `settlement_window(N)` and `strike_window(N+1)` were
 * one window, one computation written to two markets, and the client drew both
 * meanings on one shared bar. They are disjoint now — `FIXING(N+1)` runs
 * `[anchor(N+1) − W, anchor(N+1))`, *inside* N's trading window, and ends where
 * `RESOLVING(N)` begins. What overlaps instead is `RESOLVING(N)` and
 * `OPEN(N+1)`, for thirteen seconds of every sixty, with exactly one of them
 * tradable.
 *
 * **The server is the sole authority.** These predicates describe what a state
 * means; they never decide what the state *is*. If the countdown reads `0.4`
 * and a `market` frame says `EXPIRED`, the frame wins immediately and visibly
 * (D9, §2.2).
 *
 * Framework-free and exhaustive: every function switches over the full enum with
 * no `default`, so adding a state to `spec/openapi.yaml` fails the build here
 * rather than falling through to a wrong answer in a component.
 */

import type { MarketState } from '@/api/http/types'

export type { MarketState }

/**
 * Every state, in lifecycle order.
 *
 * Eleven, not the ten the plan and `CLAUDE.md` say — `REFUNDED` is the terminal
 * half of the void path (`VOIDED` means "we could not settle", `REFUNDED` means
 * "and the money is back"), it is in the D6 diagram, in `openapi.MarketState`,
 * and in the `<MarketStatusPill>` table. The count in the prose is a miscount;
 * the enum is the contract.
 */
export const MARKET_STATES: readonly MarketState[] = [
  'SCHEDULED',
  'FIXING',
  'OPEN',
  'EXPIRED',
  'RESOLVING',
  'RESOLVING_DEGRADED',
  'RESOLVED',
  'SETTLED',
  'VOIDED',
  'REFUNDED',
  'CANCELLED',
] as const

/** Which of the five phases the UI is in. The pill maps 1:1 to `MarketState`;
 *  this is the coarser grouping panels switch on. */
export type LifecyclePhase = 'PRE_TRADE' | 'TRADABLE' | 'RESOLVING' | 'TERMINAL'

export function phaseOf(state: MarketState): LifecyclePhase {
  switch (state) {
    case 'SCHEDULED':
    case 'FIXING':
      return 'PRE_TRADE'
    case 'OPEN':
      return 'TRADABLE'
    case 'EXPIRED':
    case 'RESOLVING':
    case 'RESOLVING_DEGRADED':
      return 'RESOLVING'
    case 'RESOLVED':
    case 'SETTLED':
    case 'VOIDED':
    case 'REFUNDED':
    case 'CANCELLED':
      return 'TERMINAL'
  }
  return assertNever(state)
}

/**
 * The book accepts orders in exactly one state.
 *
 * This is **necessary but not sufficient**: acceptance is gated on server
 * receive time (`server_receive_time < expiry_at_ms`), evaluated at the matching
 * engine's sequencer, and because there is no freeze that boundary is the
 * hottest race in the system. The client disables entry early and with a
 * reason; it never assumes an order will be accepted because the state looked
 * right when the button rendered.
 */
export function acceptsOrders(state: MarketState): boolean {
  return state === 'OPEN'
}

/** No position may change while a market is past expiry and not yet settled. */
export function positionsFrozen(state: MarketState): boolean {
  switch (state) {
    case 'EXPIRED':
    case 'RESOLVING':
    case 'RESOLVING_DEGRADED':
    case 'RESOLVED':
      return true
    case 'SCHEDULED':
    case 'FIXING':
    case 'OPEN':
    case 'SETTLED':
    case 'VOIDED':
    case 'REFUNDED':
    case 'CANCELLED':
      return false
  }
  return assertNever(state)
}

/** Nothing more will happen to this market. */
export function isTerminal(state: MarketState): boolean {
  switch (state) {
    case 'SETTLED':
    case 'REFUNDED':
    case 'CANCELLED':
      return true
    case 'SCHEDULED':
    case 'FIXING':
    case 'OPEN':
    case 'EXPIRED':
    case 'RESOLVING':
    case 'RESOLVING_DEGRADED':
    case 'RESOLVED':
    case 'VOIDED':
      return false
  }
  return assertNever(state)
}

/**
 * Terminal *and* immutable, which is the caching question rather than the
 * lifecycle one: a market in one of these states is served
 * `max-age=31536000, immutable` and is free forever after the first fetch (§3.3).
 */
export function isImmutable(state: MarketState): boolean {
  return state === 'SETTLED' || state === 'VOIDED' || state === 'CANCELLED' || state === 'REFUNDED'
}

/**
 * Whether a market in `state` *may* carry a strike. `strike` is written once, at
 * the `FIXING → OPEN` transition, and never again — except under `PRINT_AT_V1`
 * (register §F73), where it lands a second or two into `OPEN`, and a market that
 * voids from `OPEN` never gets one. So `true` here is "read the field", never
 * "the field is set": render `strike == null` on an `OPEN` market as pending.
 */
export function hasStrike(state: MarketState): boolean {
  switch (state) {
    case 'SCHEDULED':
    case 'FIXING':
    case 'CANCELLED':
      return false
    case 'OPEN':
    case 'EXPIRED':
    case 'RESOLVING':
    case 'RESOLVING_DEGRADED':
    case 'RESOLVED':
    case 'SETTLED':
    case 'VOIDED':
    case 'REFUNDED':
      return true
  }
  return assertNever(state)
}

/** The ~13 seconds `<ResolutionPanel>` owns: a headline moment, not a spinner. */
export function isResolving(state: MarketState): boolean {
  return state === 'RESOLVING' || state === 'RESOLVING_DEGRADED'
}

/** Settled on 6–7 of 10 samples, or a 2s gap. It still settles; it is flagged. */
export function isDegraded(state: MarketState): boolean {
  return state === 'RESOLVING_DEGRADED'
}

/** The void path: nobody was paid an outcome, everybody is refunded at cost basis. */
export function isVoidPath(state: MarketState): boolean {
  return state === 'VOIDED' || state === 'REFUNDED'
}

/**
 * `CANCELLED` is reachable only from `SCHEDULED`/`FIXING`, which is why it never
 * implies a lost position: no orders were ever accepted, so there is nothing to
 * refund.
 */
export function impliesNoPosition(state: MarketState): boolean {
  return state === 'CANCELLED'
}

// ---------------------------------------------------------------------------
// Transitions
// ---------------------------------------------------------------------------

const TRANSITIONS: Readonly<Record<MarketState, readonly MarketState[]>> = {
  SCHEDULED: ['FIXING', 'VOIDED', 'CANCELLED'],
  // Strike not fixed within 20s of the anchor → CANCELLED, nothing to refund.
  FIXING: ['OPEN', 'CANCELLED', 'VOIDED'],
  OPEN: ['EXPIRED', 'VOIDED'],
  EXPIRED: ['RESOLVING', 'VOIDED'],
  // Coverage 0.60–0.79 or a 2s gap degrades; below 0.60, a ≥3s gap, or ≥25bps
  // divergence voids — after backfill and a 20s timeout.
  RESOLVING: ['RESOLVED', 'RESOLVING_DEGRADED', 'VOIDED'],
  RESOLVING_DEGRADED: ['RESOLVED', 'VOIDED'],
  RESOLVED: ['SETTLED'],
  SETTLED: [],
  VOIDED: ['REFUNDED'],
  REFUNDED: [],
  CANCELLED: [],
}

export function nextStates(state: MarketState): readonly MarketState[] {
  return TRANSITIONS[state]
}

export function canTransition(from: MarketState, to: MarketState): boolean {
  return TRANSITIONS[from].includes(to)
}

export interface StateTransitionCheck {
  ok: boolean
  reason?: 'BACKWARDS' | 'ILLEGAL' | 'STALE_SEQ'
}

/**
 * Validate an observed transition. `state_seq` is monotonic and state never
 * moves backwards (D6 invariants), so a frame that violates either is a frame
 * to drop rather than to apply — most often a redelivery on an at-least-once
 * channel, occasionally a real ordering bug worth surfacing.
 *
 * A repeat of the same state is fine: `market.{id}` is pushed verbatim on every
 * change and re-sent on re-subscribe.
 */
export function checkTransition(
  from: { state: MarketState; stateSeq: number },
  to: { state: MarketState; stateSeq: number },
): StateTransitionCheck {
  if (to.stateSeq < from.stateSeq) return { ok: false, reason: 'STALE_SEQ' }
  if (to.state === from.state) return { ok: true }
  if (canTransition(from.state, to.state)) return { ok: true }
  // `to` sits upstream of `from`: the market is being told to go back to a
  // state it has already left. Different bug from a transition that could
  // never happen in either direction, and worth telling apart in a log.
  if (predecessorsOf(from.state).includes(to.state)) return { ok: false, reason: 'BACKWARDS' }
  return { ok: false, reason: 'ILLEGAL' }
}

/** Every state that can eventually reach `target`. */
function predecessorsOf(target: MarketState): MarketState[] {
  const out: MarketState[] = []
  for (const state of MARKET_STATES) {
    if (state === target) continue
    const seen = new Set<MarketState>()
    const stack: MarketState[] = [state]
    while (stack.length > 0) {
      const current = stack.pop() as MarketState
      if (seen.has(current)) continue
      seen.add(current)
      if (current === target) {
        out.push(state)
        break
      }
      stack.push(...TRANSITIONS[current])
    }
  }
  return out
}

function assertNever(value: never): never {
  throw new Error(`unhandled market state: ${String(value)}`)
}
